import { AddOrRemoveAssingeeParams } from '../../../params/redux/messageboard';
import { addRemoveAssigneeMessageBoardThunk } from '../../../redux/thunks/messageboard';
import CustomModal from '../../modals';
import ImageClass from '../../../assets';
import { useParams } from 'react-router';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../redux/store';
import { Avatar, Box, Button, Checkbox, IconButton, Typography } from '@mui/material';
import { DeleteOutline, PersonAddAlt } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: AddMessageBoardModal
 * @paragraph: A modal component for adding a new message board. Uses Formik for form handling
 * and integrates with Redux for message board creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const ChatMembersModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const { loaders, selectedMessageBoard } = useSelector((state: RootState) => state.messageboard);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { customers } = useSelector((state: RootState) => state.customer);
    const notAssignedUsers = customers.filter((user) => !selectedMessageBoard?.assignee?.some((assignee) => assignee.user_id === user.id));
    const assignedUsers = customers.filter((user) => selectedMessageBoard?.assignee?.some((assignee) => assignee.user_id === user.id));

    /**
     * @heading3: Functions
     * @heading4: Handles the form submission by dispatching a Redux thunk.
     * @param: {PipelineParams} values - The form values for creating a message board.
     */
    const deleteHandler = async (value: number) => {
        try {
            const data: AddOrRemoveAssingeeParams = {
                messageboard_id: selectedMessageBoard?.id || 0,
                addassignee: [],
                removeassignee: [value]
            };
            await dispatch(addRemoveAssigneeMessageBoardThunk({ data, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    const addHandler = async () => {
        if (selectedUsers.length === 0) return;
        try {
            const data: AddOrRemoveAssingeeParams = {
                messageboard_id: selectedMessageBoard?.id || 0,
                addassignee: selectedUsers,
                removeassignee: []
            };
            await dispatch(addRemoveAssigneeMessageBoardThunk({ data, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            setIsAdding(false);
            setSelectedUsers([]);
        } catch (e) {
            // Handle error
        }
    };

    return <CustomModal open={open} handleClose={loaders.isUpdating ? () => null : onClose} title={isAdding ? 'Add Members' : 'Chat Members'}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" color="textSecondary">{isAdding ? 'Select members from the list' : 'List of members added in board'}</Typography>
            {!isAdding && <Button variant="contained" startIcon={<PersonAddAlt />} onClick={() => setIsAdding(true)}>Add members</Button>}
        </Box>
        {isAdding
            ? <Box>
                {
                    notAssignedUsers.length === 0
                        ? <Typography variant="body2" color="textSecondary" align="center" mt={2}>
                            No available users to add.
                        </Typography>
                        : notAssignedUsers.map((user) => (
                            <Box key={user.id} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar
                                        src={ImageClass.userPlacedholder}
                                    />
                                    <Typography variant="body2">{`${user.first_name} ${user.last_name}`}</Typography>
                                </Box>
                                <Checkbox
                                    checked={selectedUsers.includes(user.id)}
                                    onChange={() => {
                                        if (selectedUsers.includes(user.id)) {
                                            setSelectedUsers(selectedUsers.filter((assingee_id) => assingee_id !== user.id));
                                        } else {
                                            setSelectedUsers([...selectedUsers, user.id]);
                                        }
                                    }}
                                />
                            </Box>
                        ))}
            </Box>
            : <Box display="flex" flexDirection="column" gap={1} p={2} maxHeight="250px" minHeight="250px" overflow="auto">
                {selectedMessageBoard?.assignee?.length === 0
                    ? <Typography variant="body2" color="textSecondary" align="center" mt={2}>
                        No members assigned yet.
                    </Typography>
                    : assignedUsers.map((user) => (
                        <Box key={user.id} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                    src={ImageClass.userPlacedholder}
                                />
                                <Typography variant="body2">{`${user.first_name} ${user.last_name}`}</Typography>
                            </Box>
                            {user.id === currentUser?.id
                                ? <Typography variant="body2" fontWeight="bold" color="primary">You</Typography>
                                : <IconButton onClick={() => deleteHandler(user.id)}>
                                    <DeleteOutline color="error" />
                                </IconButton>}
                        </Box>
                    ))}
            </Box>}
        {/* Action buttons for form submission or cancellation */}
        {isAdding && <Box display="flex" gap={2} mt={2}>
            <Button variant="outlined" color="primary" onClick={() => setIsAdding(false)} fullWidth disabled={loaders.isUpdating}>
                Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" fullWidth onClick={addHandler} disabled={loaders.isUpdating || selectedUsers.length === 0}>
                {loaders.isUpdating ? 'Adding...' : 'Add'}
            </Button>
        </Box>}
    </CustomModal>;
};

export default ChatMembersModal;
